import { useNavigate } from 'react-router-dom';

import { THREE } from '@/constants';
import { useCurrentUser, useView } from '@/hooks';
import { NoteOwnerType } from '@/types';

import AdminOnly from './AdminOnly';
import CardPayConversion from './CardPayConversion';
import Growth from './Growth';
import Information from './Information';
import MemberLoading from './MemberLoading';
import MomentsInsightsBanner from './MomentsInsightsBanner';
import PlanDetails from './PlanDetails';
import RecentlyUpdatedDocuments from './RecentlyUpdatedDocuments';
import SessionsOverview from './Sessions/Overview';
import TranscriptSharing from './TranscriptSharing';
import CoachInfo from './coachInfo';

import Notes from '@/components/Notes/Notes';
import SchedulingAssistants from '@/components/Profile/SchedulingAssistants';
import {
  findMomentsMemberByEmail,
  resolveMomentsBaseUrl,
  useGetMomentsCoachMembersQuery,
} from '@/store/api/momentsApi';
import {
  canAccessPlatformAdminCalendar,
  hasFeature,
  isAdmin,
  isSupport,
} from '@/types/user';

// TODO: pages should be simple, layout should take care of a good chunk, should just focus on loading components
// TODO: Components should fetch their data for the most part
// TODO: Page should only have Page components, the rest should live in `@/components`
const Member = () => {
  const { user } = useCurrentUser();
  const { selectedUser, isViewLoading, refetchSelectedUser } = useView();
  const navigate = useNavigate();

  const seeAllNotes = () => {
    navigate(`/coach/members/${selectedUser.id}/notes`);
  };
  const documentsEnabled = hasFeature(user, 'documents');
  const isAdminUser = isAdmin(user);
  const isSupportUser = isSupport(user) && !isAdminUser;
  // Same gate as Moments' admin scheduling-assistant routes.
  const canManageSchedulingAssistants = canAccessPlatformAdminCalendar(user);

  // Support users have limited read-only access
  // They should not see: Growth/Milestones, Notes, Documents, CoachInfo
  const canViewCoachingDetails = !isSupportUser;
  const momentsBaseUrl = resolveMomentsBaseUrl({
    envUrl: import.meta.env.VITE_MOMENTS_BASE_URL,
    fallbackUrl: user.momentsBaseUrl,
  });
  const momentsEnabled = !!momentsBaseUrl;
  const { data: momentsCoachMembers, isFetching: isFetchingMomentsMembers } =
    useGetMomentsCoachMembersQuery(undefined, {
      skip: !canViewCoachingDetails || !momentsEnabled || !selectedUser.email,
    });
  const momentsMember = isFetchingMomentsMembers
    ? null
    : findMomentsMemberByEmail(
        momentsCoachMembers?.members,
        selectedUser.email,
      );
  const fallbackMomentsMemberUrl = momentsEnabled
    ? `${momentsBaseUrl}/members_by_email/${encodeURIComponent(selectedUser.email)}`
    : null;
  const momentsMemberUrl = momentsMember
    ? `${momentsBaseUrl}/members/${encodeURIComponent(momentsMember.personId)}`
    : fallbackMomentsMemberUrl;

  if (isViewLoading || !selectedUser.id) {
    return <MemberLoading />;
  }

  return (
    <div className="w-full items-center">
      <div className="cement-300 flex w-full flex-1 flex-col">
        <div className="flex-1 bg-sage-100 p-0 md:p-2">
          <div className="container mx-auto max-w-screen-lg p-4">
            {canViewCoachingDetails && momentsMemberUrl && (
              <div className="mb-6">
                <MomentsInsightsBanner
                  memberFirstName={selectedUser.firstName || 'Member'}
                  momentsUrl={momentsMemberUrl}
                />
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 ">
              <div className="space-y-6 lg:col-span-2">
                <SessionsOverview user={selectedUser} />
                {documentsEnabled && canViewCoachingDetails && (
                  <RecentlyUpdatedDocuments />
                )}
                {canViewCoachingDetails && (
                  <div className="hidden lg:block">
                    <Notes
                      limit={THREE}
                      owneableId={selectedUser.id}
                      owneableType={NoteOwnerType.USER}
                      seeAllNotes={seeAllNotes}
                      showPagination={false}
                      title="Internal Notes"
                    />
                    <div className="-mx-6 mb-6 h-full divide-y divide-cement-300 border-t border-cement-300" />
                  </div>
                )}
              </div>
              <div className="space-y-6 lg:col-span-1">
                {isAdminUser && <AdminOnly client={selectedUser} />}
                {isAdminUser && <CardPayConversion userId={selectedUser.id} />}
                {canManageSchedulingAssistants && (
                  <SchedulingAssistants
                    key={selectedUser.id}
                    memberFirstName={selectedUser.firstName}
                    owner={{ kind: 'admin', memberId: selectedUser.id }}
                  />
                )}
                <Information
                  client={selectedUser}
                  onProfile={() => navigate('././././profile')}
                />
                <TranscriptSharing
                  currentUser={user}
                  member={selectedUser}
                  onUpdated={refetchSelectedUser}
                />
                <PlanDetails client={selectedUser} />
                {canViewCoachingDetails && (
                  <div className="hidden space-y-6 lg:block">
                    <CoachInfo
                      coaches={selectedUser.coaches}
                      relationships={selectedUser.coachAssignments}
                    />
                    <Growth user={selectedUser} />
                  </div>
                )}
              </div>
              {canViewCoachingDetails && (
                <div className="space-y-6 lg:hidden">
                  <CoachInfo
                    coaches={selectedUser.coaches}
                    relationships={selectedUser.coachAssignments ?? []}
                  />
                  <Growth user={selectedUser} />
                  <Notes
                    limit={THREE}
                    owneableId={selectedUser.id}
                    owneableType={NoteOwnerType.USER}
                    seeAllNotes={seeAllNotes}
                    showPagination={false}
                    title="Internal Notes"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Member;
